// src/services/smsService.ts
//
// Thin client for the admin-only /api/leads/{id}/sms endpoint.
// See backend/api/routes/sms.py for semantics.
//
// Phase 1: this client exists so the upcoming Send Listings dialog
// integration (Phase 2) has a stable API surface to call against.
// No UI consumers ship in this branch.

import { smsShortlistLinks } from "../lib/smsShortlistLinks";
import { withSmsSendIntent } from "./smsSendIntent";
import { ApiError, fetchWithAuth, refusalMessage } from "../lib/api";

export interface SendSmsRequest {
  body: string;
  shortlist_property_ids?: string[];
}

export interface SmsMessageRow {
  id: string;
  lead_id: string;
  direction: "out" | "in";
  body: string;
  status: string;
  twilio_sid: string | null;
  tracking_id: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_at: string;
}

export interface SendSmsResponse {
  message: SmsMessageRow;
  shortlist_slug: string | null;
  shortlist_url: string | null;
  tracking_id: string;
  provider_status: string | null;
  provider: string | null;
  skipped_reason: string | null;
}

export class SmsUnverifiedLeadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SmsUnverifiedLeadError";
  }
}

// The backend gates on lead.verification_provider being set; surface that as a
// typed error so the caller can prompt the admin to verify first.
//
// Ch83 · this used to parse `API error: <status> - <body>` out of the message.
// fetchWithAuth has never produced that string — the body is a FIELD on
// ApiError — so the regex never matched, the typed error was unreachable, and
// every refusal reached the operator as "API error: 400". Read the field.
export async function sendLeadSms(
  leadId: string,
  rawPayload: SendSmsRequest,
): Promise<SendSmsResponse> {
  // F27 · same chokepoint rule as sendClientSms.
  const payload = { ...rawPayload, body: smsShortlistLinks(rawPayload.body) };
  try {
    return await withSmsSendIntent(`lead:${leadId}`, payload, id => fetchWithAuth<SendSmsResponse>(`/leads/${leadId}/sms`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, send_action_id: id }),
    }), result => result.message.status === "outcome_unknown");
  } catch (err) {
    const detail = refusalMessage(err);
    if (!detail) throw err;
    // The route's own words: "Lead must be verified before SMS." Matching on
    // `verif` rather than `verified` because the sentence the server actually
    // sends conjugates it differently than the old dead branch assumed.
    if (err instanceof ApiError && err.status === 400 && /verif/i.test(detail)) {
      throw new SmsUnverifiedLeadError(detail);
    }
    throw new Error(detail);
  }
}
